import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, X, Users, Calendar, MapPin, Clock } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useUnifiedAuth } from '@/lib/auth/use-unified-auth';
import { supabase } from '@/lib/supabase/client';
import { formatDuration } from '@/lib/utils/duration';

export function BookingRequests() {
  const { user } = useUnifiedAuth();
  const [bookings, setBookings] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [actionInProgress, setActionInProgress] = React.useState<string | null>(null);

  useEffect(() => {
    if (user?.id) {
      fetchBookings();
    }
  }, [user]);

  const fetchBookings = async () => {
    try {
      if (!user?.id) {
        // Don't set error here as user might still be loading
        return;
      }

      // Try to get creator profile ID
      const { data: creatorProfile, error: profileError } = await supabase
        .from('creator_profiles')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      // If no creator profile exists, check if user is creator/admin by role
      if (profileError || !creatorProfile) {
        // Get user role to verify they should have access
        const { data: userRole, error: roleError } = await supabase
          .from('users')
          .select('role')
          .eq('id', user.id)
          .single();

        if (roleError || !userRole || (userRole.role !== 'creator' && userRole.role !== 'admin')) {
          setError('Creator profile not found. Please contact support to set up your creator profile.');
          setLoading(false);
          return;
        }

        // User is creator/admin but no creator_profile record exists
        // This is a data consistency issue - show helpful message
        setError('Creator profile setup incomplete. Please contact support to complete your creator profile setup.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      // Get paid bookings that need approval
      const { data, error: fetchError } = await supabase
        .from('bookings')
        .select(`
          id,
          user_id,
          experience_id,
          participant_count,
          total_amount,
          status,
          payment_status,
          created_at,
          special_requirements,
          experience:experiences(
            id,
            title,
            duration,
            location,
            price
          ),
          user:users!bookings_user_id_fkey(
            id,
            full_name,
            email,
            profile_image
          )
        `)
        .eq('experiences.creator_profile_id', creatorProfile.id)
        .eq('payment_status', 'paid')  // Only show PAID bookings
        .eq('status', 'pending')       // Only show pending approval
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setBookings(data || []);
    } catch (err) {
      console.error('Failed to fetch bookings:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveBooking = async (bookingId: string) => {
    try {
      setActionInProgress(bookingId);
      setError(null);

      const { error: updateError } = await supabase
        .from('bookings')
        .update({
          status: 'confirmed',
          updated_at: new Date().toISOString()
        })
        .eq('id', bookingId);

      if (updateError) throw updateError;

      // Refresh bookings list
      await fetchBookings();
    } catch (err) {
      console.error('Failed to approve booking:', err);
      setError(err instanceof Error ? err.message : 'Failed to approve booking');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleRejectBooking = async (bookingId: string) => {
    if (!window.confirm('Are you sure you want to reject this booking?')) {
      return;
    }

    try {
      setActionInProgress(bookingId);
      setError(null);

      const { error: updateError } = await supabase
        .from('bookings')
        .update({
          status: 'cancelled',
          updated_at: new Date().toISOString()
        })
        .eq('id', bookingId);

      if (updateError) throw updateError;

      // Remove the rejected booking from the local state
      setBookings(prevBookings => prevBookings.filter(b => b.id !== bookingId));
    } catch (err) {
      console.error('Failed to reject booking:', err);
      setError(err instanceof Error ? err.message : 'Failed to reject booking');
    } finally {
      setActionInProgress(null);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <p className="text-sand-400">Loading booking requests...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
        <p className="text-sm text-red-500">{error}</p>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={fetchBookings}
          className="mt-2"
        >
          Retry
        </Button>
      </div>
    );
  }

  if (!bookings?.length) {
    return (
      <div className="text-center py-12">
        <p className="text-sand-400">No pending booking requests</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {bookings.map((booking) => (
        <motion.div
          key={booking.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-earth-800/50 p-6 rounded-lg border border-earth-700/50"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-sand-400/20 to-earth-600/20 flex items-center justify-center text-sand-300 font-bold text-lg">
                {booking.user?.full_name?.charAt(0) || 'U'}
              </div>
              <div>
                <h3 className="text-lg font-medium text-sand-100">
                  {booking.user?.full_name || 'Guest'}
                </h3>
                <div className="flex items-center gap-4 text-sm text-sand-400">
                  <div className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    <span>{booking.participant_count} participant{booking.participant_count !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    <span>{new Date(booking.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="mt-1">
                  <span className="inline-flex px-2 py-1 text-xs font-medium bg-green-500/10 text-green-400 rounded">
                    Payment: {booking.payment_status}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-col items-end gap-2">
              <div className="text-right">
                <p className="text-lg font-bold text-sand-100">
                  €{booking.total_amount}
                </p>
                <p className="text-sm text-sand-400">Total Amount</p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleRejectBooking(booking.id)}
                  disabled={actionInProgress === booking.id}
                  className="text-red-500 hover:text-red-400"
                >
                  <X className="h-4 w-4 mr-2" />
                  Reject
                </Button>
                <Button
                  onClick={() => handleApproveBooking(booking.id)}
                  disabled={actionInProgress === booking.id}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve
                </Button>
              </div>
            </div>
          </div>

          <div className="mt-6 bg-earth-800 rounded-lg p-4">
            <h4 className="font-medium text-sand-100 mb-2">
              {booking.experience?.title}
            </h4>
            <div className="flex items-center gap-4 text-sm text-sand-400">
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                <span>{formatDuration(booking.experience?.duration || 0)}</span>
              </div>
              {booking.experience?.location?.city && (
                <div className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  <span>{booking.experience.location.city}</span>
                </div>
              )}
            </div>
          </div>

          {booking.special_requirements && (
            <div className="mt-4 p-3 bg-earth-700/30 rounded-lg">
              <p className="text-sm text-sand-400 mb-1">Special Requirements:</p>
              <p className="text-sand-300 text-sm">{booking.special_requirements}</p>
            </div>
          )}
        </motion.div>
      ))}
    </div>
  );
}